import { useTheme } from '@mui/material/styles';
import { Box, Container, Typography, useMediaQuery, BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import LogoSection from '../LogoSection';
import useConfig from 'hooks/useConfig';
import ProfileSection from './ProfileSection';
import { DiscordIconLight } from 'ui-component/Icons';
import { Theme } from '@mui/system';

import { useDispatch, useSelector } from 'store';
import { openDrawer } from 'store/slices/menu';
import { HomeIcon, DashboardIcon, OrdersIcon, WithDrawalIcon, MenuIcon } from 'ui-component/Icons';
import { useNavigate, useLocation } from 'react-router-dom';
import LocalizationSection from './LocalizationSection';
import { Trans } from 'react-i18next';
import environment from 'environment';

const styles = {
    container: (theme: Theme) => ({
        maxWidth: { sm: '1296px !important', xs: '100%' },
        width: '100%',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: { xs: '12px 16px', sm: '8px 24px' },
        borderRadius: { xs: 0, sm: '16px' },
        flexGrow: 1,
        backgroundColor: theme.palette.primary.main
    }),
    logoWrapper: (theme: Theme) => ({
        width: 228,
        display: 'flex',
        [theme.breakpoints.down('md')]: {
            width: 'auto'
        }
    }),
    desktopLogo: {
        display: 'flex',
        alignItems: 'end',
        gap: 2,
        flexGrow: 1
    },
    discordSection: {
        display: { sm: 'flex', xs: 'none' },
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        textDecoration: 'none'
    },
    discordText: {
        color: 'common.white'
    },
    mobileHeader: {
        display: { xs: 'block', sm: 'none' }
    },
    rightSection: {
        display: 'flex',
        alignItems: 'center',
        gap: 2
    },
    bottomNav: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        padding: '12px 6px',
        display: { xs: 'block', md: 'none' },
        '&.MuiPaper-root': {
            backgroundColor: 'primary.main',
            borderRadius: '0'
        }
    },
    bottomNavigationStyle: {
        '&.MuiButtonBase-root': {
            padding: 0,
            minWidth: 'auto',
            transition: 'transform 0.2s ease-in-out',
            '&:active': {
                color: environment.THEME_COLORS.PRIMARY,
                transform: 'scale(0)',
                fontSize: '10px !important'
            }
        },
        '& .MuiBottomNavigationAction-label': {
            fontSize: '10px',
            fontWeight: 400,
            color: 'common.white',
            mt: '2px',
            '&.Mui-selected': {
                color: environment.THEME_COLORS.PRIMARY,
                fontSize: '10px !important'
            }
        }
    },
    challengeButton: {
        backgroundColor: environment.THEME_COLORS.PRIMARY,
        boxShadow: '0px -8px 16px 0px rgba(114, 112, 255, 0.50)',
        color: 'white',
        padding: '12px',
        textTransform: 'none',
        width: '100%',
        textAlign: 'center',
        position: 'fixed',
        bottom: '63px',
        left: 0,
        right: 0,
        zIndex: 999,
        display: { xs: 'block', sm: 'none' },
        transition: 'bottom 0.3s ease-in-out'
    }
};

const Header = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const { layout } = useConfig();
    const navigate = useNavigate();
    const location = useLocation();

    const shouldShowChallengeButton = !['/competitions'].includes(location.pathname) && !['/challenges'].includes(location.pathname);

    return (
        <>
            <Container sx={styles.container(theme)}>
                <Box sx={styles.logoWrapper(theme)}>
                    <Box component="span" sx={styles.desktopLogo}>
                        <LogoSection onlyLogo={true} />
                    </Box>
                </Box>
                <Box sx={styles.rightSection}>
                    <Box ml={1} mr={1}>
                        <LocalizationSection />
                    </Box>

                    <ProfileSection />
                </Box>
            </Container>

            {shouldShowChallengeButton && environment.FEATURES.CHALLENGES_SECTION && (
                <Typography variant="h5" sx={styles.challengeButton} onClick={() => navigate('/challenges')}>
                    <Trans>checkout_section.start_a_challenge</Trans>
                </Typography>
            )}

            <Paper sx={styles.bottomNav} elevation={3}>
                <BottomNavigation
                    showLabels
                    value={location.pathname}
                    onChange={(event, newValue) => {
                        navigate(newValue);
                    }}
                    sx={{
                        '&.MuiBottomNavigation-root': {
                            backgroundColor: 'primary.main',
                            height: 'auto'
                        }
                    }}
                >
                    <BottomNavigationAction
                        sx={styles.bottomNavigationStyle}
                        label={<Trans>home</Trans>}
                        value="/home"
                        icon={<HomeIcon active={location.pathname === '/home'} />}
                    />
                    <BottomNavigationAction
                        sx={styles.bottomNavigationStyle}
                        label={<Trans>dashboard</Trans>}
                        value="/dashboard"
                        icon={<DashboardIcon active={location.pathname === '/dashboard'} />}
                    />
                    <BottomNavigationAction
                        sx={styles.bottomNavigationStyle}
                        label={<Trans>orders</Trans>}
                        value="/orders"
                        icon={<OrdersIcon active={location.pathname === '/orders'} />}
                    />
                    <BottomNavigationAction
                        sx={styles.bottomNavigationStyle}
                        label={<Trans>withdrawals</Trans>}
                        value="/withdrawals"
                        icon={<WithDrawalIcon active={location.pathname === '/withdrawals'} />}
                    />
                    <BottomNavigationAction
                        sx={styles.bottomNavigationStyle}
                        label={<Trans>menu</Trans>}
                        onClick={() => dispatch(openDrawer(!drawerOpen))}
                        icon={<MenuIcon active={location.pathname === '/menu'} />}
                    />
                </BottomNavigation>
            </Paper>
        </>
    );
};

export default Header;
